// components/OrganizationDetails.tsx

import React from "react";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader } from "@shared/ui/card";
import { Progress } from "@shared/ui/progress";
import { Tooltip, TooltipTrigger, TooltipContent } from "@shared/ui/tooltip";
import {
  Edit,
  Building2,
  Flag,
  Currency,
  Calendar as CalendarIcon,
  Clock as ClockIcon,
  Info,
} from "lucide-react";
import {
  useTenantDetail,
  useCurrencies,
  useDateFormats,
  useTimezones,
} from "../hooks";
import { OrganizationData, ChecklistField } from "../types";

interface OrganizationDetailsProps {
  onEditClick: () => void;
}

const checklistFieldMeta = {
  country: {
    icon: (
      <span className="inline-flex items-center justify-center bg-blue-100 text-blue-600 rounded-full p-1.5 mr-2">
        <Flag className="h-4 w-4" />
      </span>
    ),
    label: "Country",
  },
  currency: {
    icon: (
      <span className="inline-flex items-center justify-center bg-purple-100 text-purple-600 rounded-full p-1.5 mr-2">
        <Currency className="h-4 w-4" />
      </span>
    ),
    label: "Currency",
  },
  dateFormat: {
    icon: (
      <span className="inline-flex items-center justify-center bg-yellow-100 text-yellow-500 rounded-full p-1.5 mr-2">
        <CalendarIcon className="h-4 w-4" />
      </span>
    ),
    label: "Date Format",
  },
  timezone: {
    icon: (
      <span className="inline-flex items-center justify-center bg-green-100 text-green-600 rounded-full p-1.5 mr-2">
        <ClockIcon className="h-4 w-4" />
      </span>
    ),
    label: "Time Zone",
  },
};

const OrganizationDetails: React.FC<OrganizationDetailsProps> = ({
  onEditClick,
}) => {
  const { data: tenantData, isLoading: tenantLoading } = useTenantDetail();
  const { data: currencies } = useCurrencies();
  const { data: dateFormats } = useDateFormats();
  const { data: timezones } = useTimezones();

  if (tenantLoading) {
    return (
      <Card className="flex flex-col h-full shadow-sm">
        <CardHeader className="pb-3">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          </div>
        </CardHeader>
        <CardContent className="pt-0 flex-1">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!tenantData?.result) {
    return (
      <Card className="flex flex-col h-full shadow-sm">
        <CardContent className="pt-6 flex-1 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <Building2 className="h-8 w-8 mx-auto mb-2" />
            <p>No organization data found</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const tenant = tenantData.result;

  const organization: OrganizationData = {
    name: tenant.name || "",
    adminEmail: tenant.userSpoc?.email || "",
    contactNo: tenant.contactNumber || "",
    currency: tenant.currency || "",
    dateFormat: tenant.dateFormat || "",
    timeZone: tenant.extraDataMap?.timezone || "",
    country: tenant.addressMap?.country || "",
    state: tenant.addressMap?.state || "",
    city: tenant.addressMap?.city || "",
  };

  const checklistFields: ChecklistField[] = [
    {
      key: "country",
      value: organization.country,
      label: organization.country
        ? currencies?.result.find((cty) => cty.key === organization.country)
            ?.value || organization.country
        : "",
    },
    {
      key: "currency",
      value: organization.currency,
      label: organization.currency
        ? currencies?.result.find((cur) => cur.key === organization.currency)
            ?.value || organization.currency
        : "",
    },
    {
      key: "dateFormat",
      value: organization.dateFormat,
      label: organization.dateFormat
        ? dateFormats?.result.find((df) => df.key === organization.dateFormat)
            ?.value || organization.dateFormat
        : "",
    },
    {
      key: "timezone",
      value: organization.timeZone,
      label: organization.timeZone
        ? timezones?.result.find((tz) => tz.key === organization.timeZone)
            ?.value || organization.timeZone
        : "",
    },
  ];

  const completionCount = checklistFields.filter((f) => !!f.value).length;
  const completionPct = Math.round(
    (completionCount / checklistFields.length) * 100
  );

  return (
    <Card className="flex flex-col h-full shadow-sm">
      <CardHeader className="pb-3 flex flex-row items-center gap-2">
        <span className="inline-flex items-center justify-center bg-purple-100 text-primary rounded-full p-2 mr-1">
          <Building2 className="h-5 w-5" />
        </span>
        <span className="font-semibold text-base text-gray-900">
          Organization Details
        </span>
        <Button
          onClick={onEditClick}
          size="sm"
          className="ml-auto"
          variant="outline"
        >
          <Edit className="h-4 w-4 mr-1" />
          Edit
        </Button>
      </CardHeader>

      <CardContent className="pt-0 flex-1 flex flex-col">
        <div className="mb-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
          <div>
            <span className="text-xs text-muted-foreground">Name</span>
            <div className="font-medium text-base text-gray-900 truncate">
              {organization.name || "Not set"}
            </div>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Admin Email</span>
            <div className="text-base text-gray-900 truncate">
              {organization.adminEmail || "Not set"}
            </div>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">
              Contact Number
            </span>
            <div
              className={`text-base ${
                organization.contactNo ? "text-gray-900" : "text-gray-400"
              }`}
            >
              {organization.contactNo || <span>Not set</span>}
            </div>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Country</span>
            <div
              className={`text-base ${
                organization.country ? "text-gray-900" : "text-gray-400"
              }`}
            >
              {organization.country ? (
                currencies?.result.find(
                  (cty) => cty.key === organization.country
                )?.value || organization.country
              ) : (
                <span>Not set</span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-2 flex flex-col gap-1">
          <div className="flex items-center mb-1">
            <span className="font-semibold text-sm">Setup Checklist</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-4 h-4 text-gray-400 cursor-pointer ml-1" />
              </TooltipTrigger>
              <TooltipContent side="top">
                Complete these quick steps for a smooth setup. Easily edit
                below.
              </TooltipContent>
            </Tooltip>
            <span className="ml-auto text-xs text-muted-foreground font-medium">
              {completionCount}/{checklistFields.length} complete
            </span>
          </div>

          <Progress value={completionPct} className="h-2 bg-gray-100 my-1" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
            {checklistFields.map((field) => {
              const meta = checklistFieldMeta[field.key];
              const isDone = !!field.value;
              return (
                <div
                  key={field.key}
                  className={`flex items-start rounded-md border px-2 py-2 text-xs gap-2 min-h-[44px]
    ${
      isDone
        ? "border-green-200 bg-green-50 text-green-700"
        : "border-orange-200 bg-orange-50 text-orange-700"
    }`}
                >
                  {/* Left: Icon */}
                  <span className="shrink-0 mt-1">{meta.icon}</span>

                  {/* Right: label + badge stacked */}
                  <div className="flex flex-col">
                    <span className="font-medium">{meta.label}</span>
                    <Badge
                      variant={isDone ? "default" : "secondary"}
                      className="capitalize mt-1 w-fit"
                    >
                      {isDone ? field.label || "Set" : "Not set"}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default OrganizationDetails;
